import { useContent } from '@/hooks/useContent';
import { Link } from 'react-router-dom';

const getServices = (t: (key: string) => string, imgT: (key: string) => string) => [
  {
    title: t('services_card1_title'),
    description: t('services_card1_desc'),
    icon: 'ri-tools-line',
    image: imgT('services_preview_card1'),
    service: 'Spray Foam Insulation',
  },
  {
    title: t('services_card2_title'),
    description: t('services_card2_desc'),
    icon: 'ri-archive-line',
    image: imgT('services_preview_card2'),
    service: 'Batt Insulation',
  },
  {
    title: t('services_card3_title'),
    description: t('services_card3_desc'),
    icon: 'ri-recycle-line',
    image: imgT('services_preview_card3'),
    service: 'Cellulose Insulation',
  },
];

export default function ServicesPreview() {
  const { t } = useContent('home');
  const { t: tc } = useContent('common');
  const { t: imgT } = useContent('images');
  const services = getServices(t, imgT);

  return (
    <section className="py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row gap-10 items-center mb-14">
          <div className="flex-1">
            <span className="text-primary-500 text-xs font-semibold uppercase tracking-widest">{t('services_badge')}</span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-foreground-50 mt-3 leading-tight max-w-md">
              {t('services_title')}
            </h2>
            <p className="text-foreground-400 mt-4 text-sm leading-relaxed max-w-sm">
              {t('services_desc')}
            </p>
            <div className="inline-flex items-center gap-3 bg-background-100 border border-primary-500/30 text-foreground-50 px-6 py-3.5 rounded-xl mt-7">
              <i className="ri-map-pin-line text-primary-500 text-xl w-6 h-6 flex items-center justify-center"></i>
              <div>
                <span className="text-2xl font-extrabold text-primary-500">{t('services_area_title')}</span>
                <span className="text-foreground-50 font-semibold text-sm ml-2">{t('services_area_label')}</span>
                <p className="text-foreground-400 text-xs mt-0.5">{t('services_area_sub')}</p>
              </div>
            </div>
          </div>

          <div className="flex-1 relative">
            <div className="relative w-full h-[420px] rounded-2xl overflow-hidden">
              <img
                alt="FMO LLC insulation team in action"
                className="w-full h-full object-cover object-center"
                src={imgT('services_preview_main')}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
            </div>
            <div className="absolute bottom-5 right-5 flex flex-col gap-3 w-64">
              <div className="bg-background-100/95 backdrop-blur-sm rounded-xl px-4 py-3 flex items-start gap-3 border border-primary-500/20">
                <div className="w-9 h-9 flex items-center justify-center bg-primary-500/15 rounded-lg shrink-0 mt-0.5">
                  <i className="ri-shield-check-line text-primary-500 text-base w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h4 className="text-foreground-50 font-bold text-xs leading-tight">{t('services_overlay_reliable')}</h4>
                  <p className="text-foreground-400 text-[11px] mt-0.5 leading-relaxed">{t('services_overlay_reliable_desc')}</p>
                </div>
              </div>
              <div className="bg-background-100/95 backdrop-blur-sm rounded-xl px-4 py-3 flex items-start gap-3 border border-primary-500/20">
                <div className="w-9 h-9 flex items-center justify-center bg-primary-500/15 rounded-lg shrink-0 mt-0.5">
                  <i className="ri-file-list-3-line text-primary-500 text-base w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <h4 className="text-foreground-50 font-bold text-xs leading-tight">{t('services_overlay_free')}</h4>
                  <p className="text-foreground-400 text-[11px] mt-0.5 leading-relaxed">{t('services_overlay_free_desc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <h3 className="text-xl font-bold text-foreground-50">{t('services_section_title')}</h3>
          <p className="text-foreground-400 text-sm max-w-md">{t('services_section_desc')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((svc) => (
            <div key={svc.title} className="group bg-background-100 rounded-2xl overflow-hidden border border-white/5 hover:border-primary-500/40 transition-all duration-700 hover:-translate-y-1">
              <div className="relative w-full h-48 overflow-hidden">
                <img
                  alt={svc.title}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  src={svc.image}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all duration-500 flex items-center justify-center">
                  <Link
                    to={`/contact?service=${encodeURIComponent(svc.service)}`}
                    className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-bold px-6 py-3 rounded-full text-sm transition-all duration-300 cursor-pointer opacity-0 group-hover:opacity-100 translate-y-3 group-hover:translate-y-0 whitespace-nowrap"
                  >
                    {tc('common_request_this_service')}
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
              <div className="p-6">
                <div className="w-10 h-10 flex items-center justify-center bg-primary-500/15 rounded-xl mb-4">
                  <i className={`${svc.icon} text-primary-500 text-lg w-6 h-6 flex items-center justify-center`}></i>
                </div>
                <h3 className="text-foreground-50 font-bold text-base mb-2">{svc.title}</h3>
                <p className="text-foreground-400 text-sm leading-relaxed">{svc.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/services"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-10 py-4 rounded-full text-sm transition-colors cursor-pointer"
          >
            {tc('common_view_all')}
            <i className="ri-arrow-right-line w-5 h-5 flex items-center justify-center"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}